/** A producer with a run-list operation. */
export type RunProducer = 'COMMON_BALANCE_POOL' | 'SEGMENT_DATA_TABLE' | 'DERIVED_ALLOCATION_SHEET';

/** Mirrors `ProducerRunSource` in the API model: only MEC and dry runs keep their own copy of the results. */
export type ProducerRunSource = 'MEC' | 'DRY_RUN';

/** Mirrors `ProducerRunStatus` in the API model. */
export type ProducerRunStatus = 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED';

/** Mirrors `ProducerRun`: one run of a producer (pool, segment table, derived sheet) that kept its own copy of the results. */
export interface ProducerRun {
    /** Pass back as `executionId` on the producer's reads and download. */
    executionId: string;
    groupExecutionId?: string;
    groupNumber?: string;
    source: ProducerRunSource;
    status: ProducerRunStatus;
    /** Epoch millis. */
    startedAt?: number;
    /** Epoch millis. */
    completedAt?: number;
    requestedBy?: string;
    /** Epoch millis after which a dry run's copy is deleted. */
    expiresAt?: number;
    /** Why a FAILED run failed. Only the derived sheet's run list returns failed runs. */
    errorMessage?: string;
    /** The derived sheet version the run computed with: 0 the live one when it ran, N an archived one. Derived sheet runs only. */
    definitionVersion?: number;
}

/** What a results screen shows: the shared current numbers, or one run's own read-only copy. */
export type ExecutionSelection = { kind: 'SHARED' } | { kind: 'RUN'; run: ProducerRun };

export const SHARED_SELECTION: ExecutionSelection = Object.freeze({ kind: 'SHARED' });

const selectionKey = (s?: ExecutionSelection): string | undefined =>
    s && (s.kind === 'RUN' ? `RUN#${s.run.executionId}` : s.kind);

/** True when both name the same copy (both shared, or the same run); false while either is unresolved. */
export const sameSelection = (a?: ExecutionSelection, b?: ExecutionSelection): boolean =>
    !!a && !!b && selectionKey(a) === selectionKey(b);

/** The shared list response, e.g. `ListCommonBalancePoolRunsResponse`, `ListSegmentDataTableRunsResponse`. */
export interface ListProducerRunsResponse {
    /** One per group, newest first. */
    runs?: ProducerRun[];
    /** The run a screen opens on; absent => the shared current numbers. */
    defaultExecutionId?: string;
}
